import { alpha } from '@mui/material/styles';
import {
  DASHBOARD_ACCENT,
  getDashboardDarkSurfaceTokens,
  getDashboardPageShellSx,
} from './dashboardSurfaceSx';

export const EDIT_BOOKING_ACCENT = DASHBOARD_ACCENT;

export function getEditBookingTheme(isDark) {
  return getDashboardDarkSurfaceTokens(isDark);
}

export function getEditBookingPageShellSx(isDark, extra = {}) {
  return {
    ...getDashboardPageShellSx(isDark ? 'dark' : 'light'),
    minHeight: '100vh',
    ...extra,
  };
}

export function getEditBookingHeroPaperSx(isDark, extra = {}) {
  const tokens = getEditBookingTheme(isDark);
  const accent = EDIT_BOOKING_ACCENT;
  return {
    mb: 3,
    borderRadius: 3,
    overflow: 'hidden',
    position: 'relative',
    background: isDark
      ? `linear-gradient(135deg, ${alpha(accent, 0.14)} 0%, ${alpha(accent, 0.05)} 42%, ${tokens.surface} 100%)`
      : `linear-gradient(135deg, ${alpha(accent, 0.15)} 0%, ${alpha(accent, 0.05)} 50%, #ffffff 100%)`,
    border: `1px solid ${isDark ? alpha(accent, 0.24) : alpha(accent, 0.22)}`,
    boxShadow: isDark
      ? `0 10px 28px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.04)`
      : `0 4px 20px ${alpha(accent, 0.12)}`,
    transition: 'all 0.3s ease',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 3,
      background: `linear-gradient(90deg, ${accent} 0%, ${alpha(accent, 0.45)} 100%)`,
    },
    ...extra,
  };
}

export function getEditBookingMainPaperSx(isDark, extra = {}) {
  const tokens = getEditBookingTheme(isDark);
  return {
    p: { xs: 2, sm: 3, md: 4 },
    background: isDark ? tokens.surfaceGradient : '#ffffff',
    backgroundColor: isDark ? tokens.surface : '#ffffff',
    border: `1px solid ${tokens.surfaceBorder}`,
    borderRadius: 2,
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
    transition: 'background-color 0.3s ease, border-color 0.3s ease',
    ...extra,
  };
}

export function getEditBookingSectionCardSx(isDark, extra = {}) {
  const tokens = getEditBookingTheme(isDark);
  return {
    borderRadius: 2,
    border: `1px solid ${isDark ? alpha(EDIT_BOOKING_ACCENT, 0.18) : alpha(EDIT_BOOKING_ACCENT, 0.14)}`,
    background: isDark
      ? `linear-gradient(160deg, ${alpha(EDIT_BOOKING_ACCENT, 0.08)} 0%, ${tokens.surface} 58%)`
      : `linear-gradient(160deg, ${alpha(EDIT_BOOKING_ACCENT, 0.06)} 0%, #ffffff 58%)`,
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
    ...extra,
  };
}

export function getEditBookingPaymentCardSx(isDark, extra = {}) {
  return getEditBookingSectionCardSx(isDark, {
    position: 'sticky',
    top: 24,
    ...extra,
  });
}

export function getEditBookingDrawerShellSx(isDark, extra = {}) {
  const tokens = getEditBookingTheme(isDark);
  return {
    background: isDark ? tokens.surfaceGradient : '#ffffff',
    backgroundColor: isDark ? tokens.surface : '#ffffff',
    borderLeft: isDark ? `1px solid ${tokens.surfaceBorder}` : undefined,
    ...extra,
  };
}

export function getEditBookingDrawerHeaderSx(isDark) {
  const tokens = getEditBookingTheme(isDark);
  return {
    borderBottom: `2px solid ${tokens.surfaceBorderStrong}`,
    backgroundColor: tokens.headerBg,
  };
}

export function getEditBookingDrawerFooterSx(isDark) {
  const tokens = getEditBookingTheme(isDark);
  return {
    borderTop: `2px solid ${tokens.surfaceBorderStrong}`,
    backgroundColor: tokens.headerBg,
  };
}

export function getEditBookingInputBorderColor(isDark) {
  const tokens = getEditBookingTheme(isDark);
  return isDark ? alpha(tokens.text, 0.22) : 'rgba(0, 0, 0, 0.23)';
}

export function getEditBookingFieldSx(isDark, hasError = false, theme) {
  const borderColor = getEditBookingInputBorderColor(isDark);
  const errorColor = theme?.palette?.error?.main || '#f44336';
  return {
    '& .MuiOutlinedInput-root': {
      backgroundColor: isDark ? alpha('#ffffff', 0.03) : '#ffffff',
      '& fieldset': {
        borderColor: hasError ? errorColor : borderColor,
      },
      '&:hover fieldset': {
        borderColor: hasError ? errorColor : alpha(EDIT_BOOKING_ACCENT, isDark ? 0.35 : 0.4),
      },
      '&.Mui-focused fieldset': {
        borderColor: hasError ? errorColor : EDIT_BOOKING_ACCENT,
        borderWidth: 1,
      },
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: hasError ? errorColor : EDIT_BOOKING_ACCENT,
    },
  };
}

export function getEditBookingSelectSx(isDark) {
  const borderColor = getEditBookingInputBorderColor(isDark);
  return {
    backgroundColor: isDark ? alpha('#ffffff', 0.03) : '#ffffff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(EDIT_BOOKING_ACCENT, isDark ? 0.35 : 0.4),
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: EDIT_BOOKING_ACCENT,
    },
  };
}

export function getEditBookingOutlinedChipSx(isDark) {
  const tokens = getEditBookingTheme(isDark);
  return {
    borderColor: tokens.surfaceBorderStrong,
    color: tokens.subtleText,
    height: 22,
    fontSize: '0.7rem',
  };
}

export function getEditBookingPrimaryButtonSx(isDark, extra = {}) {
  return {
    backgroundColor: EDIT_BOOKING_ACCENT,
    color: '#04120e',
    fontWeight: 700,
    boxShadow: isDark ? `0 8px 20px ${alpha(EDIT_BOOKING_ACCENT, 0.2)}` : undefined,
    '&:hover': {
      backgroundColor: EDIT_BOOKING_ACCENT,
      opacity: 0.92,
    },
    '&:disabled': {
      backgroundColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
      color: isDark ? alpha(getEditBookingTheme(isDark).subtleText, 0.55) : '#999',
      boxShadow: 'none',
    },
    ...extra,
  };
}

export function getEditBookingAccentTextButtonSx(isDark) {
  return {
    color: EDIT_BOOKING_ACCENT,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: alpha(EDIT_BOOKING_ACCENT, isDark ? 0.12 : 0.08),
    },
  };
}

export function getEditBookingInfoTooltipButtonSx(isDark, size = 16) {
  const tokens = getEditBookingTheme(isDark);
  return {
    p: 0.25,
    ml: 0.5,
    minWidth: size + 8,
    minHeight: size + 8,
    borderRadius: '50%',
    color: alpha(tokens.subtleText, 0.85),
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      color: EDIT_BOOKING_ACCENT,
      backgroundColor: alpha(EDIT_BOOKING_ACCENT, isDark ? 0.15 : 0.12),
      transform: 'scale(1.08)',
      boxShadow: isDark ? `0 0 12px ${alpha(EDIT_BOOKING_ACCENT, 0.25)}` : `0 0 10px ${alpha(EDIT_BOOKING_ACCENT, 0.2)}`,
    },
  };
}

export function getEditBookingCounterShellSx(isDark) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 2,
    border: `1px solid ${alpha(EDIT_BOOKING_ACCENT, isDark ? 0.28 : 0.34)}`,
    backgroundColor: alpha(EDIT_BOOKING_ACCENT, isDark ? 0.08 : 0.05),
    overflow: 'hidden',
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : `0 1px 3px ${alpha(EDIT_BOOKING_ACCENT, 0.12)}`,
  };
}

export function getEditBookingTableContainerSx(isDark, extra = {}) {
  const tokens = getEditBookingTheme(isDark);
  return {
    borderRadius: 2,
    border: `1px solid ${tokens.surfaceBorder}`,
    backgroundColor: isDark ? alpha(tokens.surface, 0.72) : '#ffffff',
    ...extra,
  };
}

export function getEditBookingStatusColor(status, isDark) {
  const statusColors = {
    confirmed: { bg: alpha(EDIT_BOOKING_ACCENT, isDark ? 0.16 : 0.12), color: EDIT_BOOKING_ACCENT },
    checked_in: { bg: alpha('#2196f3', isDark ? 0.16 : 0.12), color: '#60a5fa' },
    partial_checkin: { bg: alpha('#ff9800', isDark ? 0.16 : 0.12), color: '#ffb74d' },
    checked_out: { bg: alpha('#9c27b0', isDark ? 0.16 : 0.12), color: '#ce93d8' },
    cancelled: { bg: alpha('#f44336', isDark ? 0.16 : 0.12), color: '#ef5350' },
    no_show: { bg: alpha('#94a3b8', isDark ? 0.16 : 0.12), color: '#94a3b8' },
    pending: { bg: alpha('#ffc107', isDark ? 0.16 : 0.12), color: '#ffd54f' },
  };
  return statusColors[String(status || '').toLowerCase()] || statusColors.pending;
}
